import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as dwv from "dwv";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { Spinner } from "@/shared/components/spinner";
import { Slider } from "@/shared/components/misc";
import { cn } from "@/shared/lib/utils";
import {
  getImagingStudyFileBlob,
  getImagingAnnotations,
  saveImagingAnnotations,
  ImagingFileError,
  type ImagingSeries,
  type ImagingSeriesMember,
  type AnnotationStroke,
  type AnnotationPoint,
} from "@/portals/patient/api/imagingStudiesApi";
import { readFileMeta, isJpegLsTransferSyntax, transcodeJpegLsIfNeeded } from "./dicomJpegLsTranscode";
import { ensureDwvWorkerPathsResolveCorrectly } from "./dwvWorkerSetup";

// dwv ships its own decode web workers (dist/assets/workers/*.worker.min.js,
// needed only for compressed transfer syntaxes — JPEG baseline/lossless,
// JPEG2000, RLE) as siblings of dwv.min.js, and resolves their URL at
// runtime relative to `document.baseURI` (the CURRENT PAGE's own URL, not
// the site root — see dwvWorkerSetup.ts for the exact line of dwv's real
// shipped source this comes from). Every route this viewer is reached from
// is nested at least one path segment deep, so that relative path always
// resolves wrong — silently breaking the worker path for exactly the
// compressed-transfer-syntax files that need one (this never surfaces on an
// ordinary uncompressed file, only once a real JPEG-compressed DICOM is
// opened). FIXED — see ensureDwvWorkerPathsResolveCorrectly() below/imported
// above, called once at module load, and dwvWorkerSetup.test.ts for the
// empirical proof (a real, unmocked dwv.App constructing the correct
// `/assets/workers/...` URL instead of the broken route-relative one).
ensureDwvWorkerPathsResolveCorrectly();
type ViewerPhase = "fetching" | "transcoding" | "decoding" | "ready" | "error";
type ViewerErrorKind = "decode" | "network" | "auth" | "not_found" | "other";
type ActiveTool = "WindowLevel" | "ZoomAndPan";
type AnnotationTool = "none" | "pen" | "eraser";

const ERROR_COPY: Record<ViewerErrorKind, { title: string; icon: string }> = {
  decode: { title: "Couldn't decode this image", icon: "lucide:file-warning" },
  network: { title: "Connection problem", icon: "lucide:wifi-off" },
  auth: { title: "Session expired", icon: "lucide:lock" },
  not_found: { title: "Not found", icon: "lucide:file-x" },
  other: { title: "Something went wrong", icon: "lucide:alert-circle" },
};

// Brightness and contrast are each expressed as a -100..100 offset from THIS
// image's own auto-computed default window level/width (captured into
// baseWindowLevelRef once dwv finishes loading), not as absolute pixel
// values — a fixed absolute range would be meaningless across modalities (an
// X-ray's pixel range looks nothing like a CT's). Scaling both offsets by
// the image's own default window WIDTH keeps the slider ranges feeling
// similarly sensitive regardless of what kind of study is open.
//
// This mirrors dwv's own native WindowLevel drag tool, which moves BOTH axes
// at once (horizontal drag = width/contrast, vertical drag = center/
// brightness) — previously both bars here only ever adjusted center, so
// contrast was reachable by dragging on the image but not by either slider
// at all, no matter how far they were pushed.
const BRIGHTNESS_MIN = -100;
const BRIGHTNESS_MAX = 100;
const BRIGHTNESS_DEFAULT = 0;

// Contrast narrows/widens the window WIDTH instead of shifting its center.
// Unlike brightness (which can freely shift center either direction), width
// must stay positive — CONTRAST_MIN_WIDTH_SCALE keeps the narrowest setting
// (value = CONTRAST_MAX) at 10% of the original width rather than letting it
// collapse to zero/negative, which would make the image blank instead of
// just very high-contrast.
const CONTRAST_MIN = -100;
const CONTRAST_MAX = 100;
const CONTRAST_DEFAULT = 0;
const CONTRAST_MIN_WIDTH_SCALE = 0.1;

// Cine playback — a reasonable default review speed (JUDGMENT CALL: no spec
// value given). Fast enough to see motion through a stack, slow enough to
// still make out individual frames; adjustable later via a speed control if
// ever needed, not exposed yet since the spec only asked for "play/pause +
// reasonable default fps".
const CINE_FPS = 6;
const CINE_INTERVAL_MS = Math.round(1000 / CINE_FPS);

// Eraser "hit radius", in IMAGE pixels (not screen pixels — see the
// coordinate-space note on AnnotationPoint in imagingStudiesApi.ts) — a
// whole-stroke eraser: any existing stroke with a point within this radius
// of the eraser's path is removed entirely, rather than a true per-pixel
// erase. JUDGMENT CALL: simpler and more predictable to use than a
// composite-operation pixel eraser, and matches how most lightweight
// annotation tools (not full image editors) implement "erase".
const ERASE_RADIUS_IMAGE_PX = 12;

const ANNOTATION_COLORS = ["#E4002B", "#00A7F2", "#FACC15"];
const PEN_WIDTH = 3;

// --- Prefetch / pre-decode pipeline (smooth scrubbing + cine) ---
//
// Two independent background pipelines run continuously while a series is
// open, both keyed off the CURRENT global position, both re-run every time
// that position settles somewhere new:
//
//  1. Byte prefetch — fetch nearby members' raw DICOM bytes into the same
//     blob cache the active member already uses (getCachedFileBlob/
//     cacheFileBlob), so the network round trip is usually already done by
//     the time the user actually gets there.
//  2. Decode pool — for members whose bytes are ALREADY cached, construct a
//     real (hidden) dwv.App, decode it, and hold it ready to be shown
//     instantly. This is the part that actually removes the "Decoding
//     image…" pause on nearby navigation — bytes alone aren't enough, dwv
//     still has to parse + decode pixel data through a real App instance.
//
// dwv has no offscreen/headless decode API and no way to pre-init or pool
// App instances itself (checked node_modules/dwv/dist/dwv.d.ts — App is
// always bound to one real DOM container div at init() time, via
// ViewConfig(divId); there is also no removeData/deleteData call to free a
// specific dataId's decoded pixel buffer once added to an App, only
// removeDataViewConfig which drops the VIEW/layer, not the underlying
// decoded Image object — so holding many members inside ONE ever-growing
// App would leak memory with no way to reclaim it). So each decoded member
// lives in its own dwv.App in a POOL of hidden container divs (see the JSX
// below — one persistent <div> per slot, never display:none since that
// would zero their clientWidth/Height and break dwv's own layer sizing).
// The pool holds every member of the series when it fits
// DECODED_MEMORY_BUDGET_BYTES, so nothing is ever evicted or re-decoded;
// only an over-budget series evicts, and then only frames not needed soon
// (see acquireSlot). Evicting a slot = abortAllLoads() + drop the dwv.App
// reference so its decoded Image becomes garbage-collectable.

/** How many members' RAW BYTES are proactively fetched on each side of the
 *  current position while NOT playing (while playing, fetching runs
 *  forward through the whole series — see runByteFetchWorker). Raw
 *  compressed DICOM bytes are cheap to hold (no decode, no canvas backing
 *  store) — typically tens of KB to ~1-2MB each for a normal CT/MR slice. */
const BYTE_PREFETCH_RADIUS = 8;

/** Bounded concurrency for background byte fetches. 3 is a deliberately
 *  small, fixed constant: enough parallelism to keep the pipe usefully busy
 *  on a normal connection without competing with the ACTIVE member's own
 *  fetch (which always runs immediately, outside this cap — see
 *  fetchMemberBlobDeduped) or hammering a backend that has to stream
 *  multi-MB DICOM files per request. Picked, not measured — a real backend
 *  under real network conditions may want this tuned, but 3 is a
 *  conventional "a few, not many" default for background prefetch (browsers
 *  themselves cap same-host HTTP/1.1 connections around 6; leaving headroom
 *  for the active member's own fetch plus normal UI requests). */
const BYTE_PREFETCH_CONCURRENCY = 3;

/** Decoded frames are retained for the whole series-viewing session (no
 *  re-decode when navigating back to one), up to this memory budget. Each
 *  retained dwv.App holds the decoded pixel buffer plus an image-sized RGBA
 *  ImageData and offscreen canvas; its on-screen canvas is only full-size
 *  while it's the displayed slot (hidden slots are 1px, see the slot JSX).
 *  See DECODED_BYTES_PER_PIXEL for the measured per-pixel cost. */
const DECODED_MEMORY_BUDGET_BYTES = 768 * 1024 * 1024;

/** Resident cost of one retained, hidden slot ≈ a fixed per-dwv.App overhead
 *  plus a per-pixel cost. Measured in Chromium (browser process-tree
 *  private memory, before vs after decoding every member): 83 CT slices at
 *  512x512 added ~4.4MB each; 83 PET slices at 128x128 added ~1.4MB each. */
const DECODED_BYTES_PER_MEMBER_OVERHEAD = 1024 * 1024;
const DECODED_BYTES_PER_PIXEL = 13;

/** Floor for the pool size when a series' images are so large that the
 *  budget alone would allow fewer slots than this. */
const MIN_POOL_SIZE = 7;

function estimateMemberBytes(member: ImagingSeriesMember): number {
  const rows = member.rows && member.rows > 0 ? member.rows : 512;
  const columns = member.columns && member.columns > 0 ? member.columns : 512;
  return DECODED_BYTES_PER_MEMBER_OVERHEAD + rows * columns * memberFrameCount(member) * DECODED_BYTES_PER_PIXEL;
}

/** One slot per member (nothing ever evicted) whenever the whole series fits
 *  the budget; otherwise as many as fit, never below MIN_POOL_SIZE. */
function computePoolSize(members: ImagingSeriesMember[]): number {
  const largest = members.reduce((max, m) => Math.max(max, estimateMemberBytes(m)), 1);
  const fit = Math.floor(DECODED_MEMORY_BUDGET_BYTES / largest);
  return Math.max(1, Math.min(members.length, Math.max(MIN_POOL_SIZE, fit)));
}

/** Delay before the "Seeking to frame N…" indicator appears, so a seek onto
 *  an already-decoded frame (painted within the same commit or two) never
 *  flashes it. */
const SEEK_INDICATOR_DELAY_MS = 120;

/** How long a not-yet-decoded seek target must stay put before its load
 *  starts (see the active-navigation driver). */
const SEEK_LOAD_SETTLE_MS = 150;

/** How long a frame must stay displayed before its saved annotations are
 *  requested (see the annotation-loading effect) — longer than one cine frame
 *  (CINE_INTERVAL_MS), so a frame merely passed through never requests. */
const ANNOTATIONS_SETTLE_MS = 300;
const ANNOTATIONS_TIMEOUT_MS = 30_000;

/** Bounded concurrency for BACKGROUND (non-active) decodes. Kept at 1,
 *  deliberately smaller than the byte-fetch concurrency: decoding is
 *  CPU/worker-bound rather than I/O-bound, so unlike network fetches there
 *  is little to gain from doing several at once, and doing them one at a
 *  time avoids a background decode competing with the user-facing active
 *  member's own decode for main-thread/worker time. The window still fills
 *  in steadily between navigations — it just does so one slot at a time. */
const DECODE_CONCURRENCY = 1;

/** Debounce before (re)starting background work after the current position
 *  moves. Two reasons this exists rather than retargeting on every single
 *  index change: (1) a user actively scrubbing or cine-ing moves through
 *  several positions per second — recomputing and re-kicking background
 *  work on every one of those would mean constantly abandoning
 *  almost-started work for the next position instead of ever finishing
 *  anything; (2) the ACTIVE member's own fetch/decode (which is what the
 *  user is actually waiting on right now) always runs immediately,
 *  completely outside this debounce — this delay only affects when OTHER,
 *  not-yet-requested nearby members start being proactively fetched/decoded
 *  in the background, which is imperceptible to the user at 200ms. */
const PREFETCH_SETTLE_MS = 200;

/** Hard ceiling on how long ONE member's decode is allowed to hang before
 *  it's treated as a failure. CONFIRMED necessary, not defensive paranoia:
 *  dwv's own compressed-format decode workers can fail to load under this
 *  app's Vite bundling (see loadMemberIntoSlot's own comment on this exact,
 *  observed-in-practice failure), and when that happens dwv never fires
 *  EITHER "load" or "error" — with no timeout, that slot (and, since
 *  concurrency is bounded, every background prefetch queued behind it) would
 *  hang forever with zero user-visible feedback. 20s is generous enough that
 *  a slow-but-genuinely-working decode (a very large multi-frame file, a
 *  slow connection re-fetching after a cache miss) is never falsely killed,
 *  while still bounding the worst case to something a patient will actually
 *  wait out rather than assume the app is broken and reload. */
const DECODE_TIMEOUT_MS = 20_000;

/** One pre-decode pool slot — a persistent hidden (or, if active, visible)
 *  DOM container plus whichever dwv.App is currently living in it, if any.
 *  Slots are allocated once (computePoolSize of them) and live for the whole
 *  component lifetime; only which MEMBER a slot holds changes over time. */
interface PoolSlot {
  idx: number;
  containerId: string;
  memberIndex: number | null;
  app: dwv.App | null;
  ready: boolean;
  /** This slot's own auto-computed window/level baseline, captured once at
   *  decode time — see baseWindowLevelRef's own comment for why this is
   *  needed per-image, not just once globally. Reapplied (with the user's
   *  current brightness/contrast offset) whenever this slot is promoted to
   *  active, since a different slot means a different dwv ViewController
   *  that never saw those adjustments. */
  baseWindowLevel: { center: number; width: number } | null;
  /** Bumped every time this slot is (re)assigned to a member or evicted —
   *  lets an in-flight async load notice it has been superseded (slot
   *  reused for something else) and stop touching state instead of
   *  clobbering whatever the slot now holds. */
  loadToken: number;
  /** The series-wide zoom/pan (see ViewTransform) this slot's app currently
   *  shows — null for dwv's default fit. Lets promotion skip re-applying. */
  appliedViewTransform: ViewTransform | null;
}

/** Zoom/pan exactly as dwv holds it: the layer group's scale and pan offset,
 *  plus the view layer's "absolute zoom offset" (the shift from zooming
 *  around a point, which the layer group's offset doesn't include). The
 *  same triple dwv itself copies to sync a new layer to a zoomed group. */
interface ViewTransform {
  scale: { x: number; y: number; z: number };
  offset: { x: number; y: number; z: number };
  absoluteZoomOffset: { x: number; y: number };
}

function makePoolSlots(baseId: string, size: number): PoolSlot[] {
  return Array.from({ length: size }, (_, idx) => ({
    idx,
    containerId: `${baseId}-slot-${idx}`,
    memberIndex: null,
    app: null,
    ready: false,
    baseWindowLevel: null,
    loadToken: 0,
    appliedViewTransform: null,
  }));
}

/** How many displayable frames one series member contributes — its own
 *  multi-frame count if it has one, otherwise 1 (a plain single-frame
 *  file). */
function memberFrameCount(member: ImagingSeriesMember): number {
  return member.numberOfFrames && member.numberOfFrames > 0 ? member.numberOfFrames : 1;
}

function totalFrameCount(members: ImagingSeriesMember[]): number {
  return members.reduce((sum, m) => sum + memberFrameCount(m), 0);
}

/** Maps a GLOBAL frame index (spanning every member's frames end to end) to
 *  which member file it falls in and that file's own local frame index —
 *  the one bit of arithmetic that lets a multi-file series and a
 *  multi-frame single file share one scroller/cine control (see this
 *  component's own docstring). */
function resolveGlobalIndex(
  globalIndex: number,
  members: ImagingSeriesMember[],
): { memberIndex: number; localFrame: number } {
  let remaining = globalIndex;
  for (let i = 0; i < members.length; i++) {
    const count = memberFrameCount(members[i]);
    if (remaining < count) return { memberIndex: i, localFrame: remaining };
    remaining -= count;
  }
  const lastIndex = Math.max(0, members.length - 1);
  return { memberIndex: lastIndex, localFrame: Math.max(0, memberFrameCount(members[lastIndex]) - 1) };
}

/** Inverse of resolveGlobalIndex. */
function globalIndexOf(memberIndex: number, localFrame: number, members: ImagingSeriesMember[]): number {
  let offset = 0;
  for (let i = 0; i < memberIndex; i++) offset += memberFrameCount(members[i]);
  return offset + localFrame;
}

function distance(a: AnnotationPoint, b: AnnotationPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Races `promise` against a timer — rejects with `message` if the timer
 *  wins. Used to bound any step in the load pipeline that has no built-in
 *  timeout of its own (see both call sites for why each one is a real,
 *  confirmed hang risk, not defensive paranoia). Does NOT cancel the
 *  underlying promise (JS has no way to do that generically) — it just stops
 *  this call site from waiting on it forever; the caller's own `stillLive`/
 *  `isCurrentTarget` checks are what keep a late resolution from clobbering
 *  newer state after the timeout has already fired. */
function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => { window.clearTimeout(timeoutId); resolve(value); },
      (err) => { window.clearTimeout(timeoutId); reject(err); },
    );
  });
}

/** The actual viewer body — a fresh instance is mounted (via `key` on the
 *  caller, keyed by seriesInstanceUid) every time a different series is
 *  opened, so there is always exactly one of these alive per opened series.
 *
 * Unifies two previously-separate concepts — a multi-FRAME single file, and
 * a multi-FILE series — behind one global frame index (0..totalFrameCount-1)
 * driven by either the manual scroller or the cine play control. For a
 * multi-frame file, moving it just moves dwv's own frame index
 * within the one already-loaded file (cheap, synchronous). For a multi-file
 * series, crossing a file boundary triggers fetching+decoding that member's
 * bytes (via the existing per-row GET /{id}/file endpoint — one dwv.App load
 * per member file, there is no batch-fetch endpoint) before dwv can show it
 * — the scroller/cine UI is identical either way, only the underlying data
 * fetch differs. See resolveGlobalIndex above for the index math.
 *
 * A background prefetch/pre-decode pipeline (see the constants and PoolSlot
 * docstring above) fetches nearby members' bytes and decodes them into
 * retained dwv.App instances — once decoded, a frame stays decoded for the
 * session (up to DECODED_MEMORY_BUDGET_BYTES), so revisiting it is instant.
 *
 * Two indices, deliberately separate: `targetIndex` is where the scroller /
 * cine WANTS to be; `displayedIndex` is the frame actually painted, set only
 * where a paint happens (promoteSlot, or an in-file frame seek). The counter
 * always shows `displayedIndex`, so it can never run ahead of the canvas.
 * Cine only advances onto frames that are already decoded, and shows
 * "Buffering…" (resuming the moment the frame is ready) otherwise.
 */
export function DicomViewer({
  series,
  actingAsProfileId,
  getCachedFileBlob,
  cacheFileBlob,
}: {
  series: ImagingSeries;
  actingAsProfileId?: string;
  /** Same ref-based "get cached response, else fetch and cache it" pattern
   *  UploadHistoryContext already uses for document files — reused here,
   *  keyed by a MEMBER's imaging_study id (not the series id — a series can
   *  have several members, each with its own cached blob). */
  getCachedFileBlob: (memberId: string) => Blob | undefined;
  cacheFileBlob: (memberId: string, blob: Blob) => void;
}) {
  const containerId = useId().replace(/:/g, "-");
  // The outer stage wrapper — sized by CSS/layout, observed once (for the
  // whole component's lifetime) so every pool slot's dwv.App stays
  // correctly sized even while hidden (see the prefetch/pre-decode
  // docstring above).
  const stageRef = useRef<HTMLDivElement | null>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // Always mirrors whichever pool slot is currently ACTIVE/displayed —
  // every existing toolbar/annotation function below that reads
  // appRef.current is unaffected by the pool: it just always sees
  // "whatever's on screen now".
  const appRef = useRef<dwv.App | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const unmountedRef = useRef(false);

  const members = series.files;
  const totalFrames = useMemo(() => totalFrameCount(members), [members]);

  const [phase, setPhase] = useState<ViewerPhase>("fetching");
  const [errorKind, setErrorKind] = useState<ViewerErrorKind>("other");
  const [errorMessage, setErrorMessage] = useState("");
  const [retryToken, setRetryToken] = useState(0);

  const [activeTool, setActiveTool] = useState<ActiveTool>("WindowLevel");
  const [rotation, setRotation] = useState(0);

  // The GLOBAL frame index (0..totalFrames-1) the scroller / cine WANT to
  // show. Not what's on screen — see displayedIndex.
  const [targetIndex, setTargetIndex] = useState(0);
  // The global frame index actually painted right now; null until the first
  // frame is. Set only where a paint happens (promoteSlot / seekDisplayedFrame).
  const [displayedIndex, setDisplayedIndex] = useState<number | null>(null);
  // The target's member — the one the active-navigation driver loads.
  const [loadedMemberIndex, setLoadedMemberIndex] = useState(() => resolveGlobalIndex(0, members).memberIndex);
  const [isPlaying, setIsPlaying] = useState(false);
  // True while cine is waiting for its next frame to finish decoding.
  const [isBuffering, setIsBuffering] = useState(false);
  const [showSeeking, setShowSeeking] = useState(false);

  // This image's own auto-computed window/level at load time — the "0" point
  // every brightness/contrast adjustment below is relative to. Null until
  // onLoad runs. Re-captured on every member load, but brightness/contrast
  // themselves are NOT reset when only the loaded member changes (see the
  // load effect below) — a user's chosen offset should keep applying to
  // whichever image is on screen, not silently snap back to neutral every
  // time the scroller crosses into a new file. Only a genuinely different
  // opened series (this whole component remounting via `key`) starts fresh.
  const baseWindowLevelRef = useRef<{ center: number; width: number } | null>(null);
  const [brightness, setBrightness] = useState(BRIGHTNESS_DEFAULT);
  const [contrast, setContrast] = useState(CONTRAST_DEFAULT);
  // Always-current copies for promoteSlot (see its comment). Assigned during
  // render, so they never lag the state they mirror.
  const activeToolRef = useRef(activeTool);
  activeToolRef.current = activeTool;
  const brightnessRef = useRef(brightness);
  brightnessRef.current = brightness;
  const contrastRef = useRef(contrast);
  contrastRef.current = contrast;

  // --- Annotations (Phase 4) ---
  const [annotationTool, setAnnotationTool] = useState<AnnotationTool>("none");
  const [annotationColor, setAnnotationColor] = useState(ANNOTATION_COLORS[0]);
  const [strokes, setStrokes] = useState<AnnotationStroke[]>([]);
  const [annotationsLoading, setAnnotationsLoading] = useState(false);
  // The displayedFrameKey whose saved annotations have finished loading —
  // drawing is only allowed once this matches the frame on screen.
  const [annotationsReadyFor, setAnnotationsReadyFor] = useState<string | null>(null);
  const [annotationsSaving, setAnnotationsSaving] = useState(false);
  const [annotationsError, setAnnotationsError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const [lastSavedStrokes, setLastSavedStrokes] = useState<AnnotationStroke[]>([]);
  const drawingStrokeRef = useRef<AnnotationStroke | null>(null);
  const isPointerDownRef = useRef(false);

  const dirty = useMemo(
    () => JSON.stringify(strokes) !== JSON.stringify(lastSavedStrokes),
    [strokes, lastSavedStrokes],
  );

  // `resolved` is the TARGET's member/frame; `displayedResolved` is what's
  // actually on screen (annotations, save, and the counter follow this one).
  const resolved = useMemo(() => resolveGlobalIndex(targetIndex, members), [targetIndex, members]);
  const displayedResolved = useMemo(
    () => (displayedIndex === null ? null : resolveGlobalIndex(displayedIndex, members)),
    [displayedIndex, members],
  );
  const hasDisplay = displayedResolved !== null;
  const activeMember = displayedResolved ? members[displayedResolved.memberIndex] : undefined;
  const displayedFrameKey = displayedResolved && activeMember ? `${activeMember.id}:${displayedResolved.localFrame}` : null;
  // A manual seek whose target isn't painted yet.
  const seekPending = !isPlaying && displayedIndex !== null && targetIndex !== displayedIndex;

  // --- Prefetch / pre-decode pool bookkeeping (see docstring above) ---
  const slotsRef = useRef<PoolSlot[] | null>(null);
  if (!slotsRef.current) slotsRef.current = makePoolSlots(containerId, computePoolSize(members));
  const [activeSlotIdx, setActiveSlotIdx] = useState<number | null>(null);

  // Ref mirrors of frequently-changing values/props, read from the
  // long-lived recursive background scheduler functions below (byte-fetch
  // worker, decode-pool filler) — these functions are re-created on every
  // render like any other, but their recursive continuations (registered
  // inside a `.then()`/`.finally()` that may run renders later) must always
  // act on the LATEST position/props, not whatever was captured when that
  // particular continuation happened to be scheduled.
  const resolvedRef = useRef(resolved);
  const membersRef = useRef(members);
  const loadedMemberIndexRef = useRef(loadedMemberIndex);
  const activeSlotIdxRef = useRef<number | null>(activeSlotIdx);
  const actingAsProfileIdRef = useRef(actingAsProfileId);
  const getCachedFileBlobRef = useRef(getCachedFileBlob);
  const cacheFileBlobRef = useRef(cacheFileBlob);
  const displayedIndexRef = useRef(displayedIndex);
  const totalFramesRef = useRef(totalFrames);
  const isPlayingRef = useRef(isPlaying);
  useEffect(() => {
    resolvedRef.current = resolved;
    membersRef.current = members;
    loadedMemberIndexRef.current = loadedMemberIndex;
    activeSlotIdxRef.current = activeSlotIdx;
    actingAsProfileIdRef.current = actingAsProfileId;
    getCachedFileBlobRef.current = getCachedFileBlob;
    cacheFileBlobRef.current = cacheFileBlob;
    displayedIndexRef.current = displayedIndex;
    totalFramesRef.current = totalFrames;
  });

  const lastRetryTokenRef = useRef(0);

  // Series-wide zoom/pan (null = dwv's default fit). One value for the whole
  // series, applied to whichever frame is shown — never per-frame state. A
  // new series remounts this component, so it starts at the default.
  const viewTransformRef = useRef<ViewTransform | null>(null);
  const applyingViewTransformRef = useRef(false);

  // Cine scheduling state (see playStep).
  const playTimerRef = useRef<number | null>(null);
  // When the pending (or currently running) cine step is due, as a
  // performance.now() time — the next one is due CINE_INTERVAL_MS after it.
  const nextPlayDueRef = useRef(0);
  // The global frame cine is waiting on while buffering, else null.
  const bufferingForRef = useRef<number | null>(null);
  const playStepRef = useRef<() => void>(() => {});

  // De-duplicates raw-byte fetches: both the active member's own load and
  // the background byte-prefetch scheduler call this, so a member whose
  // fetch the background scheduler already started (before the user
  // navigated there) is simply awaited/reused instead of being fetched a
  // second time — this IS the "don't compete destructively with
  // user-initiated navigation" behavior for bytes: navigating to a member
  // that's already mid-prefetch never starts a redundant request, it just
  // rides the one already in flight.
  const inFlightBlobFetchesRef = useRef<Map<string, Promise<Blob>>>(new Map());
  function fetchMemberBlobDeduped(memberId: string): Promise<Blob> {
    const cached = getCachedFileBlobRef.current(memberId);
    if (cached) return Promise.resolve(cached);
    const inFlight = inFlightBlobFetchesRef.current.get(memberId);
    if (inFlight) return inFlight;
    const promise = getImagingStudyFileBlob(memberId, actingAsProfileIdRef.current)
      .then((blob) => {
        cacheFileBlobRef.current(memberId, blob);
        inFlightBlobFetchesRef.current.delete(memberId);
        return blob;
      })
      .catch((err) => {
        inFlightBlobFetchesRef.current.delete(memberId);
        throw err;
      });
    inFlightBlobFetchesRef.current.set(memberId, promise);
    return promise;
  }

  // Background byte-fetch scheduler state.
  const byteFetchActiveRef = useRef(0);
  const failedPrefetchRef = useRef<Set<string>>(new Set());
  const fetchGenerationRef = useRef(0);
  // Background decode-pool scheduler state.
  const decodeActiveRef = useRef(0);

  /** How soon a member will be needed from the current target position:
   *  while playing, forward distance (wrapping, since cine loops) — so the
   *  frames just played are the ones "needed last"; otherwise plain distance
   *  either side. Drives fetch/decode order and, for an over-budget series,
   *  eviction order. */
  function neededIn(memberIndex: number): number {
    const center = resolvedRef.current.memberIndex;
    const n = membersRef.current.length;
    return isPlayingRef.current ? (memberIndex - center + n) % n : Math.abs(memberIndex - center);
  }

  /** Picks a pool slot for `memberIndex`: a free one if there is one.
   *  Otherwise (only possible for a series over DECODED_MEMORY_BUDGET_BYTES)
   *  evicts the occupied slot needed LAST (see neededIn) — never the
   *  displayed slot, and never the slot loading the current target. A
   *  background decode only evicts something needed later than the frame it
   *  wants to decode, so it can't thrash frames that are about to be shown. */
  function acquireSlot(memberIndex: number, background = false): PoolSlot | null {
    const slots = slotsRef.current!;
    const free = slots.find((s) => s.memberIndex === null);
    if (free) {
      assignSlot(free, memberIndex);
      return free;
    }
    const evictable = slots
      .filter((s) => s.idx !== activeSlotIdxRef.current && s.memberIndex !== loadedMemberIndexRef.current)
      .sort((a, b) => neededIn(b.memberIndex!) - neededIn(a.memberIndex!));
    const victim = evictable[0];
    if (!victim) return null;
    if (background && neededIn(victim.memberIndex!) <= neededIn(memberIndex)) return null;
    assignSlot(victim, memberIndex);
    return victim;
  }

  function assignSlot(slot: PoolSlot, memberIndex: number) {
    slot.app?.abortAllLoads();
    slot.app = null;
    slot.ready = false;
    slot.baseWindowLevel = null;
    slot.memberIndex = memberIndex;
    slot.loadToken += 1;
    slot.appliedViewTransform = null;
  }

  /** Records the displayed frame's zoom/pan as the series-wide one. Wired to
   *  every app's zoomchange/offsetchange, but only the displayed app's user
   *  interaction counts — not our own applyViewTransform, not hidden apps. */
  function captureViewTransform(app: dwv.App, slot: PoolSlot) {
    if (applyingViewTransformRef.current || appRef.current !== app) return;
    const group = app.getLayerGroupByDivId(slot.containerId);
    const layer = viewLayerFor(app);
    if (!group || !layer) return;
    const { x: sx, y: sy, z: sz } = group.getScale();
    const { x: ox, y: oy, z: oz } = group.getOffset();
    const { x: zx, y: zy } = layer.getAbsoluteZoomOffset();
    const transform = { scale: { x: sx, y: sy, z: sz }, offset: { x: ox, y: oy, z: oz }, absoluteZoomOffset: { x: zx, y: zy } };
    viewTransformRef.current = transform;
    slot.appliedViewTransform = transform;
  }

  /** Makes a slot's app show the series-wide zoom/pan: back to dwv's default,
   *  then the same offset → scale → initScale sequence dwv uses to sync a new
   *  layer to an already-zoomed group. */
  function applyViewTransform(slot: PoolSlot) {
    const app = slot.app;
    const target = viewTransformRef.current;
    if (!app || slot.appliedViewTransform === target) return;
    const group = app.getLayerGroupByDivId(slot.containerId);
    const layer = viewLayerFor(app);
    if (!group || !layer) return;
    applyingViewTransformRef.current = true;
    try {
      group.resetZoomPan();
      if (target) {
        group.setOffset(target.offset);
        group.setScale(target.scale);
        layer.initScale(target.scale, target.absoluteZoomOffset);
      }
      group.draw();
    } finally {
      applyingViewTransformRef.current = false;
    }
    slot.appliedViewTransform = target;
  }

  /** Promotes an ALREADY-ready slot to be the visible/active one — the
   *  "instant" path: no fetch, no decode, just point every existing
   *  toolbar/annotation/cine helper (all of which read appRef.current) at
   *  this slot's app instead, and reapply the user's current
   *  brightness/contrast offset to its own baseline window/level (a
   *  different slot means a different dwv ViewController that never saw
   *  those adjustments). */
  function promoteSlot(slot: PoolSlot) {
    if (!slot.app || slot.memberIndex === null) return;
    appRef.current = slot.app;
    setActiveSlotIdx(slot.idx);
    // Read from refs, not render state: promoteSlot often runs inside a load
    // callback created renders earlier (a seek that was still loading), and
    // the user may have changed tool/brightness/contrast since — the frame
    // being shown must get the CURRENT ones, matching what the toolbar shows.
    slot.app.setTool(activeToolRef.current);
    const vc = activeLayerControllerFor(slot.app);
    if (vc && slot.baseWindowLevel) {
      baseWindowLevelRef.current = slot.baseWindowLevel;
      const b = brightnessRef.current;
      const c = contrastRef.current;
      if (b !== BRIGHTNESS_DEFAULT || c !== CONTRAST_DEFAULT) {
        const base = slot.baseWindowLevel;
        const newCenter = base.center + (b / 100) * base.width;
        const widthScale = Math.max(CONTRAST_MIN_WIDTH_SCALE, 1 - c / 100);
        vc.setWindowLevel(new dwv.WindowLevel(newCenter, base.width * widthScale));
      }
    }
    // This is the paint: the slot becomes visible (and full-size, see the
    // resize layout effect) in the same commit the counter moves to it.
    const target = resolvedRef.current;
    const localFrame = target.memberIndex === slot.memberIndex ? target.localFrame : 0;
    seekLocalFrame(slot.app, localFrame);
    setDisplayedIndex(globalIndexOf(slot.memberIndex, localFrame, membersRef.current));
    setPhase("ready");
  }

  /** Moves a multi-frame file's dwv view to `localFrame` (no-op for a
   *  single-frame file). */
  function seekLocalFrame(app: dwv.App, localFrame: number) {
    const vc = activeLayerControllerFor(app);
    if (!vc) return;
    const current = vc.getCurrentIndex().getValues();
    if (current.length < 4 || current[current.length - 1] === localFrame) return;
    const next = [...current];
    next[next.length - 1] = localFrame;
    vc.setCurrentIndex(new dwv.Index(next));
  }

  function isGlobalFrameReady(globalIndex: number): boolean {
    const { memberIndex } = resolveGlobalIndex(globalIndex, membersRef.current);
    const slot = slotsRef.current!.find((s) => s.memberIndex === memberIndex);
    return !!(slot && slot.ready && slot.app);
  }

  /** Called whenever a slot finishes decoding — if cine is buffering on that
   *  member, resume right now rather than at the next tick. */
  function notifySlotReady(memberIndex: number) {
    const waitingFor = bufferingForRef.current;
    if (waitingFor === null) return;
    if (resolveGlobalIndex(waitingFor, membersRef.current).memberIndex !== memberIndex) return;
    if (playTimerRef.current !== null) {
      window.clearTimeout(playTimerRef.current);
      playTimerRef.current = null;
    }
    // Resuming from buffering starts a fresh cadence from now.
    nextPlayDueRef.current = performance.now();
    playStepRef.current();
  }

  /** Called when a load fails. If cine was buffering on that member it can
   *  never become ready on its own — stop playing and make it the target, so
   *  the normal foreground load retries it and shows its real error. */
  function notifySlotFailed(memberIndex: number) {
    const waitingFor = bufferingForRef.current;
    if (waitingFor === null || !isPlayingRef.current) return;
    if (resolveGlobalIndex(waitingFor, membersRef.current).memberIndex !== memberIndex) return;
    setIsPlaying(false);
    setTargetIndex(waitingFor);
  }

  /** The one real load pipeline (fetch → JPEG-LS transcode if needed →
   *  construct+init a dwv.App in this slot's own container → loadFiles →
   *  await load/error) — shared by BOTH the user-facing active load (which
   *  drives the visible fetching/transcoding/decoding/ready/error phase UI)
   *  and silent background decode-pool fills, so the pipeline itself never
   *  has two different implementations to keep in sync.
   *
   *  There is deliberately no frozen "isActive" flag: `isCurrentTarget()`
   *  re-checks, at EVERY step, whether `memberIndex` is what the user is
   *  CURRENTLY positioned at (via loadedMemberIndexRef, updated every
   *  render). This means a load that started as silent background
   *  prefetch will start driving the visible phase UI (and promote on
   *  completion) the moment the user actually navigates to it while it's
   *  still in flight — and, symmetrically, a load that started as the
   *  user-facing active target quietly stops touching visible state (but
   *  keeps running to completion and joins the pool) if the user moves on
   *  before it finishes. `myToken` is the separate, simpler check for
   *  whether THIS SLOT still belongs to this call at all (it may have been
   *  evicted/reassigned entirely) — background work must stop touching
   *  `slot.*` the moment that happens, regardless of relevance. */
  function loadMemberIntoSlot(slot: PoolSlot, memberIndex: number): Promise<void> {
    const member = membersRef.current[memberIndex];
    if (!member) return Promise.resolve();
    const myToken = slot.loadToken;
    const stillLive = () => !unmountedRef.current && slot.loadToken === myToken;
    const isCurrentTarget = () =>
      stillLive() && slot.memberIndex === memberIndex && loadedMemberIndexRef.current === memberIndex;

    if (isCurrentTarget()) {
      setPhase(getCachedFileBlobRef.current(member.id) ? "decoding" : "fetching");
    }

    return fetchMemberBlobDeduped(member.id)
      .then(async (blob) => {
        if (!stillLive()) return;
        let fileBuffer = await blob.arrayBuffer();
        if (!stillLive()) return;

        const meta = readFileMeta(new Uint8Array(fileBuffer));
        const needsJpegLsTranscode = meta !== null && isJpegLsTransferSyntax(meta.transferSyntaxUid);
        if (needsJpegLsTranscode) {
          if (isCurrentTarget()) setPhase("transcoding");
          try {
            // Same reasoning as DECODE_TIMEOUT_MS below, applied to the OTHER
            // place this pipeline can hang with zero feedback: the
            // hand-rolled parser/WASM-decode step this calls has only been
            // exercised against clean synthetic test fixtures — a real
            // clinical file's exact byte structure can differ in ways that
            // trip up a hand-rolled binary parser (an unexpected sequence,
            // an edge case in fragment layout) that a synthetic fixture
            // never exposes. Without a bound here, a stuck parse/decode hung
            // forever with no error and no timeout at all (unlike the dwv
            // load step below, which already had one) — CONFIRMED as a real,
            // observed failure mode, not theoretical.
            fileBuffer = await withTimeout(
              transcodeJpegLsIfNeeded(fileBuffer),
              DECODE_TIMEOUT_MS,
              "This image took too long to convert and was stopped. It may use an unsupported variant of its compression format.",
            );
          } catch (transcodeErr) {
            if (!stillLive()) return;
            if (isCurrentTarget()) {
              setErrorKind("decode");
              setErrorMessage(
                transcodeErr instanceof Error
                  ? `We couldn't decode this image: ${transcodeErr.message}`
                  : "We couldn't decode this image. It may use an unsupported format, or the file may be corrupted.",
              );
              setPhase("error");
            }
            slot.memberIndex = null;
            notifySlotFailed(memberIndex);
            return;
          }
          if (!stillLive()) return;
        }

        if (isCurrentTarget()) setPhase("decoding");

        const file = new File([fileBuffer], member.originalFilename || "study.dcm", {
          type: "application/dicom",
        });

        slot.app?.abortAllLoads();
        const app = new dwv.App();
        slot.app = app;

        const viewConfig = new dwv.ViewConfig(slot.containerId);
        const options = new dwv.AppOptions({ "*": [viewConfig] });
        options.tools = {
          WindowLevel: new dwv.ToolConfig(),
          ZoomAndPan: new dwv.ToolConfig(),
        };
        app.init(options);
        app.setImageSmoothing(false);
        app.onResize();
        const onViewTransformChange = () => captureViewTransform(app, slot);
        app.addEventListener("zoomchange", onViewTransformChange);
        app.addEventListener("offsetchange", onViewTransformChange);

        await new Promise<void>((resolve) => {
          // CONFIRMED REAL BUG, not theoretical: dwv's own decode workers for
          // compressed transfer syntaxes (JPEG baseline, classic JPEG
          // lossless, JPEG2000, RLE — anything reaching dwv that ISN'T
          // JPEG-LS, which is already converted to uncompressed before this
          // point) used to fail to load under this app's nested routing (see
          // this file's own top-of-file comment and dwvWorkerSetup.ts — now
          // fixed via ensureDwvWorkerPathsResolveCorrectly()) — and when that
          // worker never starts, dwv never fires "load" OR "error": this
          // promise used to just hang forever, freezing this slot (and,
          // since the pipeline has bounded concurrency, everything queued
          // behind it) with no way out. A hard timeout converts an infinite
          // hang into an honest, bounded error instead — this is a safety
          // net for ANY reason dwv might never call back (a genuinely
          // corrupt/unsupported file, a network-level worker-script fetch
          // failure, etc.), not just the now-fixed worker-path issue, so it
          // stays in place even with that root cause fixed.
          let settled = false;
          const timeoutId = window.setTimeout(() => {
            if (settled || !stillLive()) return;
            settled = true;
            app.removeEventListener("load", onLoad);
            app.removeEventListener("error", onError);
            if (isCurrentTarget()) {
              setErrorKind("decode");
              setErrorMessage(
                "This image took too long to decode and was stopped. It may use an unsupported compression format.",
              );
              setPhase("error");
            }
            slot.memberIndex = null;
            notifySlotFailed(memberIndex);
            resolve();
          }, DECODE_TIMEOUT_MS);
          const onLoad = () => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeoutId);
            if (!stillLive()) {
              resolve();
              return;
            }
            const vc = activeLayerControllerFor(app);
            const wl = vc?.getWindowLevel();
            if (wl) slot.baseWindowLevel = { center: wl.center, width: wl.width };
            slot.ready = true;
            if (isCurrentTarget()) promoteSlot(slot);
            notifySlotReady(memberIndex);
            resolve();
          };
          const onError = (event: unknown) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeoutId);
            if (!stillLive()) {
              resolve();
              return;
            }
            const message =
              event && typeof event === "object" && "error" in event
                ? String((event as { error?: { message?: string } }).error?.message ?? "")
                : "";
            if (isCurrentTarget()) {
              setErrorKind("decode");
              setErrorMessage(
                message
                  ? `We couldn't decode this image: ${message}`
                  : "We couldn't decode this image. It may use an unsupported format, or the file may be corrupted.",
              );
              setPhase("error");
            }
            slot.memberIndex = null;
            notifySlotFailed(memberIndex);
            resolve();
          };
          app.addEventListener("load", onLoad);
          app.addEventListener("error", onError);
          app.loadFiles([file]);
        });
      })
      .catch((err) => {
        if (stillLive()) {
          if (isCurrentTarget()) {
            if (err instanceof ImagingFileError) {
              setErrorKind(err.kind);
              setErrorMessage(err.message);
            } else {
              setErrorKind("other");
              setErrorMessage("Something went wrong loading this file.");
            }
            setPhase("error");
          }
          // Free the slot on any failure (fetch or decode) so a later
          // retarget/retry acquires a fresh slot instead of finding this
          // one permanently "occupied" by a member that never loaded.
          slot.memberIndex = null;
          notifySlotFailed(memberIndex);
        }
      });
  }

  /** Decodes, in the background, whichever member with already-fetched
   *  bytes is needed soonest (see neededIn) and isn't decoded yet — it never
   *  itself triggers a network fetch (that's the byte-prefetch worker's
   *  job). Decoded members are retained (see acquireSlot), so this drains
   *  everything fetched. One background decode in flight at a time
   *  (DECODE_CONCURRENCY). */
  function tryFillDecodeWindow() {
    if (unmountedRef.current) return;
    if (decodeActiveRef.current >= DECODE_CONCURRENCY) return;
    const list = membersRef.current;
    let best: number | undefined;
    let bestNeed = Infinity;
    for (let i = 0; i < list.length; i++) {
      if (i === loadedMemberIndexRef.current) continue; // the active member has its own pipeline already
      if (slotsRef.current!.some((s) => s.memberIndex === i)) continue; // already assigned/ready/loading
      if (!getCachedFileBlobRef.current(list[i].id)) continue; // bytes not ready yet
      const need = neededIn(i);
      if (need < bestNeed) {
        bestNeed = need;
        best = i;
      }
    }
    if (best === undefined) return;
    const slot = acquireSlot(best, true);
    if (!slot) return;
    decodeActiveRef.current += 1;
    loadMemberIntoSlot(slot, best).finally(() => {
      decodeActiveRef.current -= 1;
      tryFillDecodeWindow();
    });
  }

  /** One background byte-fetch worker "thread" — pulls the not-yet-cached,
   *  not-yet-in-flight member needed soonest, fetches it, then recurses for
   *  more. While idle it stays within BYTE_PREFETCH_RADIUS either side;
   *  while playing it keeps fetching forward through the whole series
   *  (wrapping), the way a video buffers ahead. Always continues under the
   *  LATEST generation, so a retarget never leaves fewer than
   *  BYTE_PREFETCH_CONCURRENCY workers running. */
  function runByteFetchWorker(generation: number) {
    if (unmountedRef.current || generation !== fetchGenerationRef.current) return;
    if (byteFetchActiveRef.current >= BYTE_PREFETCH_CONCURRENCY) return;
    const center = resolvedRef.current.memberIndex;
    const list = membersRef.current;
    const playing = isPlayingRef.current;
    let best: number | undefined;
    let bestNeed = Infinity;
    for (let i = 0; i < list.length; i++) {
      if (!playing && Math.abs(i - center) > BYTE_PREFETCH_RADIUS) continue;
      const member = list[i];
      if (getCachedFileBlobRef.current(member.id) || inFlightBlobFetchesRef.current.has(member.id)) continue;
      if (failedPrefetchRef.current.has(member.id)) continue;
      const need = neededIn(i);
      if (need < bestNeed) {
        bestNeed = need;
        best = i;
      }
    }
    if (best === undefined) return;
    const picked = best;
    byteFetchActiveRef.current += 1;
    fetchMemberBlobDeduped(list[picked].id)
      .catch(() => {
        // Not retried in the background (a permanently failing file would
        // otherwise be re-requested in a loop). If the user navigates there,
        // the foreground load retries for real and shows the actual error;
        // if cine is buffering on it, notifySlotFailed hands it to that path.
        failedPrefetchRef.current.add(list[picked].id);
        notifySlotFailed(picked);
      })
      .finally(() => {
        byteFetchActiveRef.current -= 1;
        if (unmountedRef.current) return;
        tryFillDecodeWindow(); // bytes just landed — opportunistically decode
        runByteFetchWorker(fetchGenerationRef.current); // keep pulling more background work
      });
  }

  /** Tops background fetching up to its concurrency cap and starts a decode
   *  if one is free — safe to call any time (no-op when saturated). */
  function kickBackgroundWork() {
    if (unmountedRef.current) return;
    const toSpawn = Math.max(0, BYTE_PREFETCH_CONCURRENCY - byteFetchActiveRef.current);
    for (let i = 0; i < toSpawn; i++) runByteFetchWorker(fetchGenerationRef.current);
    tryFillDecodeWindow();
  }

  /** One cine step: advance to the next frame ONLY if it's already decoded;
   *  otherwise enter "Buffering…" and wait — notifySlotReady resumes the
   *  instant that frame's decode completes. Based on the DISPLAYED frame,
   *  never the target, so cine can't run ahead of what's been painted. */
  function playStep() {
    playTimerRef.current = null;
    if (!isPlayingRef.current || unmountedRef.current) return;
    const current = displayedIndexRef.current ?? 0;
    const next = current + 1 >= totalFramesRef.current ? 0 : current + 1;
    if (isGlobalFrameReady(next)) {
      bufferingForRef.current = null;
      setIsBuffering(false);
      setTargetIndex(next);
      // Fixed cadence from when this step was DUE, not from now — otherwise
      // each step's own render time adds up and playback runs slow. Never
      // earlier than now, so a late step doesn't trigger a catch-up burst.
      const now = performance.now();
      const due = Math.max(now, nextPlayDueRef.current + CINE_INTERVAL_MS);
      nextPlayDueRef.current = due;
      playTimerRef.current = window.setTimeout(() => playStepRef.current(), due - now);
    } else {
      bufferingForRef.current = next;
      setIsBuffering(true);
    }
    kickBackgroundWork();
  }
  playStepRef.current = playStep;

  // Active-navigation driver — replaces what used to be a single "Step 1"
  // effect tearing down and recreating one shared dwv.App per member. Now:
  // if the target member is ALREADY a ready pool slot (background pre-decode
  // got there first), promote it instantly — no fetch, no decode, no
  // spinner. Otherwise, if it's already BEING loaded (a background fill
  // that hasn't finished, or a previous attempt), don't start a duplicate
  // pipeline for the same slot — just make sure the busy UI reflects it
  // right away; the in-flight pipeline's own isCurrentTarget() checks (see
  // loadMemberIntoSlot) will pick up that this is now relevant and drive
  // further phase transitions + the eventual promotion. Only when NEITHOR
  // applies (a genuinely fresh target, e.g. a far jump outside the window)
  // does this acquire a slot and start a brand new load — always
  // immediately, never queued behind background work (see acquireSlot).
  useEffect(() => {
    const memberIndex = loadedMemberIndex;
    const member = members[memberIndex];
    if (!member) return;
    const existingSlot = slotsRef.current!.find((s) => s.memberIndex === memberIndex);
    if (existingSlot?.ready && existingSlot.app) {
      promoteSlot(existingSlot);
      return;
    }
    if (existingSlot) {
      setPhase(getCachedFileBlob(member.id) ? "decoding" : "fetching");
      return;
    }
    const startLoad = () => {
      const slot = acquireSlot(memberIndex);
      if (slot) loadMemberIntoSlot(slot, memberIndex);
    };
    // With a frame already on screen, wait for the target to settle before
    // starting a fresh load: a drag passes over dozens of undecoded frames,
    // and loading each one would queue the landing frame's fetch behind all
    // of them (browsers run ~6 requests per host at once). The very first
    // load, and an explicit retry, start immediately.
    if (displayedIndexRef.current === null || retryToken !== lastRetryTokenRef.current) {
      lastRetryTokenRef.current = retryToken;
      startLoad();
      return;
    }
    const t = window.setTimeout(startLoad, SEEK_LOAD_SETTLE_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedMemberIndex, retryToken]);

  // Step 2 — whenever the GLOBAL index moves to a different member than the
  // one currently loaded, kick off loading that member (the active-driver
  // effect above).
  useEffect(() => {
    if (resolved.memberIndex !== loadedMemberIndex) {
      setLoadedMemberIndex(resolved.memberIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved.memberIndex]);

  // Background retarget — debounced (see PREFETCH_SETTLE_MS) so a user
  // actively scrubbing/cine-ing doesn't cause a constant churn of abandoned
  // background work; once the position has settled, bump the byte-fetch
  // generation (spawning fresh workers up to the concurrency cap) and
  // attempt a decode-pool fill pass.
  useEffect(() => {
    const t = window.setTimeout(() => {
      fetchGenerationRef.current += 1;
      const generation = fetchGenerationRef.current;
      const toSpawn = Math.max(0, BYTE_PREFETCH_CONCURRENCY - byteFetchActiveRef.current);
      for (let i = 0; i < toSpawn; i++) runByteFetchWorker(generation);
      tryFillDecodeWindow();
    }, PREFETCH_SETTLE_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved.memberIndex]);

  // Persistent stage-level ResizeObserver. Only the displayed slot follows
  // the stage size — hidden slots are a fixed 1px (see the slot JSX) and are
  // refitted when promoted (see the activeSlotIdx layout effect below).
  useEffect(() => {
    const ro = new ResizeObserver(() => {
      if (unmountedRef.current) return;
      const active = activeSlotIdxRef.current;
      if (active !== null) slotsRef.current![active].app?.onResize();
      if (stageRef.current && overlayCanvasRef.current) {
        overlayCanvasRef.current.width = stageRef.current.clientWidth;
        overlayCanvasRef.current.height = stageRef.current.clientHeight;
      }
    });
    if (stageRef.current) ro.observe(stageRef.current);
    resizeObserverRef.current = ro;
    return () => {
      ro.disconnect();
      resizeObserverRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Unmount cleanup — required resource discipline: stop every pool slot's
  // dwv.App and let the background schedulers' own generation/unmounted
  // checks (see runByteFetchWorker/tryFillDecodeWindow) stop them from
  // scheduling any further work. Fetches already in flight when unmount
  // happens still resolve (getImagingStudyFileBlob has no abort signal —
  // see its own docstring), but nothing NEW gets scheduled afterward.
  //
  // CONFIRMED REAL BUG, not theoretical: `unmountedRef.current` must be
  // reset to false at the top of this effect, not just initialized to
  // false via useRef. React 18 StrictMode (dev only, see main.tsx) mounts
  // every component, runs every effect's cleanup once (simulating an
  // unmount), then re-runs every effect again on the SAME instance/refs —
  // specifically to catch state that isn't correctly re-initialized on a
  // remount. Without this reset, that phantom cleanup permanently flips
  // unmountedRef.current to true (nothing else ever set it back), so every
  // stillLive()/isCurrentTarget() check in loadMemberIntoSlot fails FOREVER
  // afterward — silently abandoning the in-flight active fetch the moment
  // it resolves (no error, no retry, nothing) and short-circuiting every
  // background byte-fetch/decode-window worker before they ever start.
  // This is the exact, reproduced cause of the viewer sitting on "Fetching
  // file…" forever even though the network request itself completed.
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      slotsRef.current!.forEach((s) => {
        s.app?.abortAllLoads();
        s.app = null;
      });
    };
  }, []);

  // Promotion changes which slot is full-size (see the slot JSX): refit the
  // newly displayed app to the stage and shrink the previous one back to
  // 1px, before the browser paints — so the canvas swap and the counter
  // change (both in the same commit) land in the same frame.
  const prevActiveSlotIdxRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    const prev = prevActiveSlotIdxRef.current;
    prevActiveSlotIdxRef.current = activeSlotIdx;
    if (activeSlotIdx !== null) {
      const slot = slotsRef.current![activeSlotIdx];
      slot.app?.onResize();
      // After the refit, so the zoom is applied to the final canvas size —
      // and still before paint, so an unzoomed frame never flashes.
      applyViewTransform(slot);
    }
    if (prev !== null && prev !== activeSlotIdx) slotsRef.current![prev].app?.onResize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlotIdx]);

  // A target move WITHIN the displayed file (a multi-frame file's frames)
  // needs no load — seek dwv and mark it displayed in the same pre-paint
  // pass. Moves to another file are painted by promoteSlot instead.
  useLayoutEffect(() => {
    if (activeSlotIdx === null) return;
    const slot = slotsRef.current![activeSlotIdx];
    if (!slot.ready || !slot.app || slot.memberIndex !== resolved.memberIndex) return;
    seekLocalFrame(slot.app, resolved.localFrame);
    setDisplayedIndex(targetIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetIndex, activeSlotIdx]);

  // Saved annotations for the displayed frame. Loaded only once a frame has
  // stayed on screen for ANNOTATIONS_SETTLE_MS and never while playing — a
  // request per frame at cine speed piled up faster than this backend answers
  // them (~3.4s each), timed out unused, and took browser connections away
  // from the image downloads. A request for a frame that's no longer shown is
  // aborted, freeing its connection. The previous frame's strokes are cleared
  // immediately so they're never drawn over (or saved onto) another frame.
  useEffect(() => {
    setStrokes([]);
    setLastSavedStrokes([]);
    setAnnotationsError(null);
    setJustSaved(false);
    setAnnotationsReadyFor(null);
    if (!displayedFrameKey || !activeMember || !displayedResolved || isPlaying) {
      setAnnotationsLoading(false);
      return;
    }
    setAnnotationsLoading(true);
    const key = displayedFrameKey;
    const memberId = activeMember.id;
    const frame = displayedResolved.localFrame;
    const controller = new AbortController();
    let superseded = false;
    let timeoutId: number | undefined;
    const startId = window.setTimeout(() => {
      // Supplying a signal opts out of apiFetch's own timeout — keep one.
      timeoutId = window.setTimeout(() => controller.abort(), ANNOTATIONS_TIMEOUT_MS);
      getImagingAnnotations(memberId, frame, actingAsProfileId, controller.signal)
        .then((result) => {
          if (superseded) return;
          setStrokes(result.strokes);
          setLastSavedStrokes(result.strokes);
        })
        .catch(() => {
          if (superseded) return;
          setAnnotationsError("Couldn't load saved annotations for this frame.");
        })
        .finally(() => {
          window.clearTimeout(timeoutId);
          if (superseded) return;
          setAnnotationsLoading(false);
          setAnnotationsReadyFor(key);
        });
    }, ANNOTATIONS_SETTLE_MS);
    return () => {
      superseded = true;
      window.clearTimeout(startId);
      window.clearTimeout(timeoutId);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedFrameKey, isPlaying]);

  // Cine playback — drives the same targetIndex the scroller does, one
  // playStep per CINE_INTERVAL_MS, looping at the end. playStep only ever
  // advances onto an already-decoded frame (see its docstring); background
  // fetch/decode switch to forward-buffering while playing (see neededIn).
  useEffect(() => {
    isPlayingRef.current = isPlaying;
    const clearTimer = () => {
      if (playTimerRef.current !== null) {
        window.clearTimeout(playTimerRef.current);
        playTimerRef.current = null;
      }
    };
    if (!isPlaying) {
      clearTimer();
      bufferingForRef.current = null;
      setIsBuffering(false);
      return;
    }
    kickBackgroundWork();
    nextPlayDueRef.current = performance.now() + CINE_INTERVAL_MS;
    playTimerRef.current = window.setTimeout(() => playStepRef.current(), CINE_INTERVAL_MS);
    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  // "Seeking to frame N…" only after a short delay, so a seek onto an
  // already-decoded frame never flashes it.
  useEffect(() => {
    if (!seekPending) {
      setShowSeeking(false);
      return;
    }
    const t = window.setTimeout(() => setShowSeeking(true), SEEK_INDICATOR_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [seekPending]);

  // The overlay canvas only exists once a frame is displayed (see its
  // conditional render below) — size it the moment it mounts, not just on
  // the next actual container resize.
  useEffect(() => {
    if (hasDisplay && stageRef.current && overlayCanvasRef.current) {
      overlayCanvasRef.current.width = stageRef.current.clientWidth;
      overlayCanvasRef.current.height = stageRef.current.clientHeight;
    }
  }, [hasDisplay, phase]);

  // --- Overlay redraw loop (annotations) ---
  // Re-renders the annotation overlay every animation frame while the
  // viewer is ready — rather than trying to hook dwv's own internal pan/zoom
  // events (this dwv version exposes no public "the view transform changed"
  // event to listen for) — so strokes stay visually pinned to the image
  // through zoom/pan/window-level/rotation changes. Cheap at the stroke
  // counts a single annotation session realistically has. NOTE: only
  // verified logically against dwv's documented coordinate-transform API
  // (ViewLayer.displayToPlaneIndex / planePosToDisplay) — not exercised
  // against a live pan/zoom gesture in a real browser in this environment;
  // see this task's final report.
  useEffect(() => {
    if (!hasDisplay || phase === "error") return;
    let raf = 0;
    const draw = () => {
      renderAnnotationOverlay();
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDisplay, phase, strokes, annotationColor]);

  function activeLayerControllerFor(app: dwv.App) {
    const dataIds = app.getDataIds();
    const dataId = dataIds[dataIds.length - 1];
    return dataId ? app.getViewLayersByDataId(dataId)[0]?.getViewController() : undefined;
  }
  function activeLayerController() {
    const app = appRef.current;
    return app ? activeLayerControllerFor(app) : undefined;
  }
  function viewLayerFor(app: dwv.App) {
    const dataIds = app.getDataIds();
    const dataId = dataIds[dataIds.length - 1];
    return dataId ? app.getViewLayersByDataId(dataId)[0] : undefined;
  }
  function activeViewLayer() {
    const app = appRef.current;
    return app ? viewLayerFor(app) : undefined;
  }

  function renderAnnotationOverlay() {
    const canvas = overlayCanvasRef.current;
    const viewLayer = activeViewLayer();
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!viewLayer) return;

    const allStrokes = drawingStrokeRef.current ? [...strokes, drawingStrokeRef.current] : strokes;
    for (const stroke of allStrokes) {
      if (stroke.points.length === 0) continue;
      ctx.strokeStyle = stroke.tool === "eraser" ? "rgba(255,255,255,0.6)" : stroke.color;
      ctx.lineWidth = stroke.width;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      let started = false;
      for (const pt of stroke.points) {
        const display = viewLayer.planePosToDisplay(new dwv.Point2D(pt.x, pt.y));
        const dx = display.getX();
        const dy = display.getY();
        if (dx === undefined || dy === undefined || Number.isNaN(dx) || Number.isNaN(dy)) continue;
        if (!started) {
          ctx.moveTo(dx, dy);
          started = true;
        } else {
          ctx.lineTo(dx, dy);
        }
      }
      if (started) ctx.stroke();
    }
  }

  function toImagePoint(evt: React.PointerEvent<HTMLCanvasElement>): AnnotationPoint | null {
    const viewLayer = activeViewLayer();
    const canvas = overlayCanvasRef.current;
    if (!viewLayer || !canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = evt.clientX - rect.left;
    const y = evt.clientY - rect.top;
    const idx = viewLayer.displayToPlaneIndex(new dwv.Point2D(x, y));
    const values = idx.getValues();
    if (values.length < 2) return null;
    return { x: values[0], y: values[1] };
  }

  const handlePointerDown = (evt: React.PointerEvent<HTMLCanvasElement>) => {
    if (annotationTool === "none") return;
    // Until this frame's saved strokes are known, a new stroke could be
    // overwritten by them arriving, or saving could overwrite them.
    if (annotationsReadyFor !== displayedFrameKey) return;
    const pt = toImagePoint(evt);
    if (!pt) return;
    // Not implemented in jsdom (see this feature's test suite) — guarded
    // rather than assumed, since it's also simply absent on some older
    // touch browsers; drawing still works fine without pointer capture, it
    // just won't keep tracking the stroke if the pointer leaves the canvas
    // mid-drag on those environments.
    (evt.target as HTMLCanvasElement).setPointerCapture?.(evt.pointerId);
    isPointerDownRef.current = true;
    if (annotationTool === "pen") {
      drawingStrokeRef.current = { tool: "pen", color: annotationColor, width: PEN_WIDTH, points: [pt] };
    } else {
      eraseAt(pt);
    }
  };

  const handlePointerMove = (evt: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isPointerDownRef.current) return;
    const pt = toImagePoint(evt);
    if (!pt) return;
    if (annotationTool === "pen" && drawingStrokeRef.current) {
      drawingStrokeRef.current.points.push(pt);
    } else if (annotationTool === "eraser") {
      eraseAt(pt);
    }
  };

  const handlePointerUp = () => {
    if (!isPointerDownRef.current) return;
    isPointerDownRef.current = false;
    if (annotationTool === "pen" && drawingStrokeRef.current) {
      const finished = drawingStrokeRef.current;
      drawingStrokeRef.current = null;
      if (finished.points.length > 1) {
        setStrokes((prev) => [...prev, finished]);
      }
    }
  };

  function eraseAt(pt: AnnotationPoint) {
    setStrokes((prev) => prev.filter((s) => !s.points.some((p) => distance(p, pt) < ERASE_RADIUS_IMAGE_PX)));
  }

  const handleSetAnnotationTool = (tool: AnnotationTool) => {
    // Drawing and dwv's own drag tools are mutually exclusive (the overlay
    // canvas only captures pointer events while a pen/eraser is active —
    // see its pointer-events class below), and cine playback doesn't make
    // sense mid-annotation.
    setIsPlaying(false);
    setAnnotationTool((prev) => (prev === tool ? "none" : tool));
  };

  const handleSaveAnnotations = () => {
    if (!activeMember || !displayedResolved || annotationsSaving) return;
    setAnnotationsSaving(true);
    setAnnotationsError(null);
    saveImagingAnnotations(activeMember.id, displayedResolved.localFrame, strokes, actingAsProfileId)
      .then(() => {
        // Deliberately the local `strokes` just submitted, NOT the
        // server's response strokes: the backend never stores/returns
        // each stroke's `width` (cosmetic-only, see AnnotationStroke's own
        // docstring), so `result.strokes` always comes back missing that
        // field. Using it here made `dirty`'s JSON.stringify comparison
        // (below) permanently unequal after every save — the "unsaved
        // annotations" banner never cleared even though the save genuinely
        // succeeded. A successful PUT always saves exactly what was sent,
        // so `strokes` IS the new "last saved" state by definition.
        setLastSavedStrokes(strokes);
        setJustSaved(true);
        window.setTimeout(() => setJustSaved(false), 600);
      })
      .catch(() => setAnnotationsError("Couldn't save your annotations. Please try again."))
      .finally(() => setAnnotationsSaving(false));
  };

  const handleDiscardAnnotations = () => {
    setStrokes(lastSavedStrokes);
  };

  const handleSetTool = (tool: ActiveTool) => {
    appRef.current?.setTool(tool);
    setActiveTool(tool);
    // Picking a view tool leaves Pen/Eraser — otherwise the drawing overlay
    // keeps capturing the mouse and the chosen tool never receives it.
    // Any unsaved strokes stay (the unsaved banner still guards them).
    setAnnotationTool("none");
  };

  // dwv has no built-in "rotate the 2D view" tool/API in this version — see
  // the module docstring above. Rotating the layer-group container itself
  // via CSS is the standard workaround.
  const handleRotate = () => setRotation((r) => (r + 90) % 360);

  const handleReset = () => {
    const app = appRef.current;
    if (!app) return;
    // Series-wide: every other frame picks the default up when it's shown.
    applyingViewTransformRef.current = true;
    try {
      app.resetZoomPan();
    } finally {
      applyingViewTransformRef.current = false;
    }
    viewTransformRef.current = null;
    const shown = slotsRef.current!.find((s) => s.app === app);
    if (shown) shown.appliedViewTransform = null;
    activeLayerController()?.resetWindowLevel();
    setRotation(0);
    setBrightness(BRIGHTNESS_DEFAULT);
    setContrast(CONTRAST_DEFAULT);
    const wl = activeLayerController()?.getWindowLevel();
    if (wl) baseWindowLevelRef.current = { center: wl.center, width: wl.width };
  };

  // Applies BOTH axes together, since dwv's WindowLevel always needs a
  // center and a width at once.
  const applyWindowLevel = (brightnessValue: number, contrastValue: number) => {
    const vc = activeLayerController();
    const base = baseWindowLevelRef.current;
    if (!vc || !base) return;
    const newCenter = base.center + (brightnessValue / 100) * base.width;
    const widthScale = Math.max(CONTRAST_MIN_WIDTH_SCALE, 1 - contrastValue / 100);
    const newWidth = base.width * widthScale;
    vc.setWindowLevel(new dwv.WindowLevel(newCenter, newWidth));
  };

  const handleBrightnessChange = (value: number) => {
    setBrightness(value);
    applyWindowLevel(value, contrast);
  };

  const handleContrastChange = (value: number) => {
    setContrast(value);
    applyWindowLevel(brightness, value);
  };

  // Manual scroller — moving it always stops any running cine playback and
  // jumps straight to that frame (per spec: the two controls share one
  // state, never a separate desynced position). Blocked entirely while
  // there are unsaved annotation strokes on the current frame — see the
  // "unsaved annotations" banner below for the resolve path (Save/Discard).
  const handleScrollerChange = (value: number) => {
    if (dirty) return;
    setIsPlaying(false);
    setTargetIndex(value);
  };

  const handleTogglePlay = () => {
    if (dirty) return;
    // Playing leaves Pen/Eraser, same as picking a view tool.
    setAnnotationTool("none");
    setIsPlaying((p) => !p);
  };

  const isBusy = phase === "fetching" || phase === "transcoding" || phase === "decoding";
  // Once a frame is painted it stays on screen while other frames load —
  // the full-screen busy overlay is only for the very first load.
  const showInitialLoadOverlay = isBusy && !hasDisplay;
  const showViewer = hasDisplay && phase !== "error";
  const canAdjustWindowLevel = showViewer && activeTool === "WindowLevel" && baseWindowLevelRef.current !== null;
  const hasScroller = totalFrames > 1;

  const toolbarButtonRestingClass =
    "!h-8 !rounded-full !border-0 !bg-transparent !text-xs !font-semibold !text-[#0A2947] !shadow-none hover:!bg-[#FFF9FA] hover:!text-[#0A2947]";
  const toolbarButtonActiveClass =
    "!h-8 !rounded-full !border-0 !bg-[#E4002B]/50 !text-xs !font-semibold !text-white !shadow-none";

  return (
    <div className="flex h-full min-h-0 flex-col">
      {showViewer && (
        <div className="mb-3 flex shrink-0 flex-wrap items-center gap-2">
          <Button
            variant="link"
            size="sm"
            onClick={() => handleSetTool("WindowLevel")}
            className={activeTool === "WindowLevel" && annotationTool === "none" ? toolbarButtonActiveClass : toolbarButtonRestingClass}
          >
            <Icon icon="lucide:sun-medium" className="mr-1.5 h-3.5 w-3.5" />
            Window / Level
          </Button>
          <Button
            variant="link"
            size="sm"
            onClick={() => handleSetTool("ZoomAndPan")}
            className={activeTool === "ZoomAndPan" && annotationTool === "none" ? toolbarButtonActiveClass : toolbarButtonRestingClass}
          >
            <Icon icon="lucide:hand" className="mr-1.5 h-3.5 w-3.5" />
            Zoom &amp; Pan
          </Button>
          <Button variant="link" size="sm" onClick={handleRotate} className={toolbarButtonRestingClass}>
            <Icon icon="lucide:rotate-cw" className="mr-1.5 h-3.5 w-3.5" />
            Rotate
          </Button>

          <span className="mx-1 h-5 w-px bg-[#E5E7EB]" />

          <Button
            variant="link"
            size="sm"
            onClick={() => handleSetAnnotationTool("pen")}
            className={annotationTool === "pen" ? toolbarButtonActiveClass : toolbarButtonRestingClass}
          >
            <Icon icon="lucide:pen-line" className="mr-1.5 h-3.5 w-3.5" />
            Pen
          </Button>
          <Button
            variant="link"
            size="sm"
            onClick={() => handleSetAnnotationTool("eraser")}
            className={annotationTool === "eraser" ? toolbarButtonActiveClass : toolbarButtonRestingClass}
          >
            <Icon icon="lucide:eraser" className="mr-1.5 h-3.5 w-3.5" />
            Eraser
          </Button>
          <Button variant="link" size="sm" onClick={handleReset} className={toolbarButtonRestingClass}>
            <Icon icon="lucide:refresh-ccw" className="mr-1.5 h-3.5 w-3.5" />
            Reset view
          </Button>
          {annotationTool === "pen" && (
            <div className="flex items-center gap-1.5">
              {ANNOTATION_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  aria-label={`Pen color ${color}`}
                  onClick={() => setAnnotationColor(color)}
                  className={cn(
                    "h-5 w-5 shrink-0 rounded-full border-2",
                    annotationColor === color ? "border-[#0A2947]" : "border-transparent",
                  )}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          )}
          <Button
            variant="link"
            size="sm"
            onClick={handleSaveAnnotations}
            loading={annotationsSaving}
            disabled={!dirty}
            className={toolbarButtonRestingClass}
          >
            {!annotationsSaving && <Icon icon={justSaved ? "lucide:check" : "lucide:save"} className="mr-1.5 h-3.5 w-3.5" />}
            {justSaved ? "Saved" : "Save annotations"}
          </Button>
          {annotationsLoading && (
            <span className="flex items-center gap-1 text-[11px] font-medium text-[#9CA3AF]">
              <Icon icon="lucide:loader-2" className="h-3 w-3 animate-spin" />
              Loading annotations…
            </span>
          )}
        </div>
      )}

      {annotationsError && (
        <p className="mb-2 flex shrink-0 items-center gap-1.5 text-xs font-semibold text-[#DC2626]">
          <Icon icon="lucide:alert-circle" className="h-3.5 w-3.5 shrink-0" />
          {annotationsError}
        </p>
      )}

      {dirty && (
        <div className="mb-2 flex shrink-0 items-center justify-between gap-3 rounded-xl bg-[#FFFBEB] px-3 py-2 text-xs font-semibold text-[#92400E]">
          <span className="flex items-center gap-1.5">
            <Icon icon="lucide:alert-triangle" className="h-3.5 w-3.5 shrink-0" />
            You have unsaved annotations on this frame. Save or discard them before switching frames.
          </span>
          <span className="flex shrink-0 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDiscardAnnotations}
              className="!h-7 !rounded-full !border-[#92400E]/40 !bg-white !px-3 !text-[11px] !font-bold !text-[#92400E]"
            >
              Discard
            </Button>
            <Button
              size="sm"
              onClick={handleSaveAnnotations}
              loading={annotationsSaving}
              className="!h-7 !rounded-full !bg-[#92400E] !px-3 !text-[11px] !font-bold !text-white"
            >
              Save
            </Button>
          </span>
        </div>
      )}

      <div
        ref={stageRef}
        className={cn(
          "relative flex min-h-[360px] flex-1 items-center justify-center overflow-hidden rounded-2xl bg-black",
          showViewer && activeTool === "ZoomAndPan" && annotationTool === "none" && "cursor-grab active:cursor-grabbing",
        )}
      >
        {/* One persistent container per pool slot (see the prefetch/pre-
            decode docstring above). Only the displayed slot is full-size
            and visible; every other slot is 1px, so its dwv view canvas
            (sized to its container) costs next to nothing while hidden —
            that's what makes retaining every decoded frame affordable.
            Never display:none (that would zero clientWidth/Height and break
            dwv's own layer sizing). Rotation lives on the shared wrapper so
            a newly promoted slot doesn't animate its own rotation in. */}
        <div
          className="absolute inset-0 transition-transform duration-150"
          style={{ transform: rotation ? `rotate(${rotation}deg)` : undefined }}
        >
          {slotsRef.current!.map((slot) => {
            const isActive = slot.idx === activeSlotIdx;
            return (
              <div
                key={slot.containerId}
                id={slot.containerId}
                className="absolute left-0 top-0 overflow-hidden"
                style={{
                  width: isActive ? "100%" : 1,
                  height: isActive ? "100%" : 1,
                  visibility: isActive && showViewer ? "visible" : "hidden",
                  zIndex: isActive ? 1 : 0,
                }}
              />
            );
          })}
        </div>

        {showViewer && (
          <canvas
            ref={overlayCanvasRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
            className={cn(
              "absolute inset-0 h-full w-full",
              annotationTool !== "none" ? "cursor-crosshair" : "pointer-events-none",
            )}
            style={{ transform: `rotate(${rotation}deg)`, pointerEvents: annotationTool !== "none" ? "auto" : "none", zIndex: 2 }}
          />
        )}

        {showInitialLoadOverlay && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white" style={{ zIndex: 3 }}>
            <Spinner />
            <p className="text-xs font-medium">
              {phase === "fetching"
                ? "Fetching file…"
                : phase === "transcoding"
                  ? "Converting image format…"
                  : "Decoding image…"}
            </p>
          </div>
        )}

        {/* Non-blocking status over the still-displayed frame: cine waiting
            on its next frame, or a manual seek whose target isn't painted
            yet. Neither is the counter — the counter only ever shows the
            painted frame. */}
        {showViewer && ((isPlaying && isBuffering) || (showSeeking && seekPending)) && (
          <div
            className="pointer-events-none absolute left-1/2 top-3 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-black/70 px-3 py-1 text-[11px] font-semibold text-white"
            style={{ zIndex: 4 }}
          >
            <Icon icon="lucide:loader-2" className="h-3 w-3 shrink-0 animate-spin" />
            <span>{isPlaying && isBuffering ? "Buffering…" : `Seeking to frame ${targetIndex + 1}…`}</span>
          </div>
        )}

        {phase === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black px-6 text-center text-white" style={{ zIndex: 3 }}>
            <Icon icon={ERROR_COPY[errorKind].icon} className="h-8 w-8 text-[#F17893]" />
            <p className="text-sm font-bold">{ERROR_COPY[errorKind].title}</p>
            <p className="max-w-sm text-xs text-white/70">{errorMessage}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRetryToken((t) => t + 1)}
              // The "outline" variant renders on antd's default button type,
              // which has a white background — !text-white here used to put
              // white text on that same white background (invisible). The
              // background was never actually transparent/dark despite
              // !border-white/30 suggesting otherwise; fixed by making the
              // text this app's standard dark color instead of touching the
              // background, per explicit instruction to keep behavior as-is.
              className="!mt-1 !h-8 !rounded-full !border-white/30 !text-xs !font-semibold !text-[#0A2947] hover:!bg-white/10"
            >
              Try again
            </Button>
          </div>
        )}

        {canAdjustWindowLevel && (
          <div className="absolute right-3 top-1/2 hidden -translate-y-1/2 flex-col items-center gap-2 rounded-2xl bg-black/60 px-2 py-3 backdrop-blur-sm sm:flex" style={{ zIndex: 3 }}>
            <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-white/80">
              <Icon icon="lucide:sun-medium" className="h-3 w-3" />
              <span>Brightness</span>
            </div>
            <Icon icon="lucide:sun" className="h-3.5 w-3.5 shrink-0 text-white/80" />
            <Slider
              vertical
              style={{ height: 140 }}
              min={BRIGHTNESS_MIN}
              max={BRIGHTNESS_MAX}
              step={1}
              value={[brightness]}
              onValueChange={([v]) => handleBrightnessChange(v)}
            />
            <Icon icon="lucide:sun-dim" className="h-3.5 w-3.5 shrink-0 text-white/50" />
          </div>
        )}

        {canAdjustWindowLevel && (
          <div className="absolute bottom-3 left-1/2 flex w-[calc(100%-96px)] max-w-xs -translate-x-1/2 flex-col items-center gap-1.5 rounded-2xl bg-black/60 px-4 py-2.5 backdrop-blur-sm" style={{ zIndex: 3 }}>
            <div className="flex items-center gap-1 self-start text-[10px] font-semibold uppercase tracking-wide text-white/80">
              <Icon icon="lucide:contrast" className="h-3 w-3" />
              <span>Contrast</span>
            </div>
            <div className="flex w-full items-center gap-2">
              <Icon icon="lucide:contrast" className="h-3.5 w-3.5 shrink-0 text-white/50" />
              <Slider
                min={CONTRAST_MIN}
                max={CONTRAST_MAX}
                step={1}
                value={[contrast]}
                onValueChange={([v]) => handleContrastChange(v)}
                className="flex-1"
              />
              <Icon icon="lucide:contrast" className="h-3.5 w-3.5 shrink-0 text-white/80" />
            </div>
          </div>
        )}
      </div>

      {/* Scroller + cine control — shown for EITHER a multi-frame single
          file or a multi-file series, driving the same targetIndex either
          way (see this component's own docstring). */}
      {hasDisplay && hasScroller && (
        <div className="mt-4 flex shrink-0 items-center gap-3">
          <Button
            variant="ghost"
            onClick={handleTogglePlay}
            disabled={dirty}
            aria-label={isPlaying ? "Pause" : "Play"}
            className="!flex !h-8 !w-8 !shrink-0 !items-center !justify-center !rounded-full !bg-[#F3F4F6] !p-0 hover:!bg-[#E5E7EB]"
          >
            <Icon icon={isPlaying ? "lucide:pause" : "lucide:play"} className="h-4 w-4 !text-[#0A2947]" />
          </Button>
          <Icon icon="lucide:layers" className="h-4 w-4 shrink-0 text-[#9CA3AF]" />
          {/* The thumb follows the user's own seek while one is pending
              (it's their hand on it); otherwise it sits on the painted
              frame. No value tooltip — the counter is the only frame
              number, and it's authoritative. */}
          <Slider
            value={[seekPending ? targetIndex : (displayedIndex ?? 0)]}
            min={0}
            max={totalFrames - 1}
            step={1}
            disabled={dirty}
            hideTooltip
            onValueChange={([v]) => handleScrollerChange(v)}
            className="flex-1"
          />
          <span className="w-16 shrink-0 text-right text-xs font-semibold text-[#6B7280]">
            {(displayedIndex ?? 0) + 1} / {totalFrames}
          </span>
        </div>
      )}
    </div>
  );
}
